import React, { useEffect, useState } from 'react';
import AuthBrandHeader from './AuthBrandHeader';
import AuthModeTabs from './AuthModeTabs';
import SignInForm from './SignInForm';
import SignUpForm from './SignUpForm';
import './Login.css';

/**
 * Overlay Sign in / Sign up dialog (secret events and other in-place auth).
 *
 * @param {{ open: boolean; initialMode?: 'signin' | 'signup'; onClose: () => void; onSuccess?: (uid?: string, profile?: object | null) => void | Promise<void> }} props
 */
const AuthModal = ({ open, initialMode = 'signin', onClose, onSuccess = null }) => {
  const [mode, setMode] = useState(initialMode === 'signup' ? 'signup' : 'signin');

  useEffect(() => {
    if (open) {
      setMode(initialMode === 'signup' ? 'signup' : 'signin');
    }
  }, [open, initialMode]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);

    const html = document.documentElement;
    const { body } = document;
    const prev = {
      htmlOverflow: html.style.overflow,
      htmlOverflowX: html.style.overflowX,
      bodyOverflow: body.style.overflow,
      bodyOverflowX: body.style.overflowX,
    };
    html.style.overflow = 'hidden';
    html.style.overflowX = 'hidden';
    body.style.overflow = 'hidden';
    body.style.overflowX = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      html.style.overflow = prev.htmlOverflow;
      html.style.overflowX = prev.htmlOverflowX;
      body.style.overflow = prev.bodyOverflow;
      body.style.overflowX = prev.bodyOverflowX;
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleSuccess = async (uid, profile) => {
    if (typeof onSuccess === 'function') {
      await onSuccess(uid, profile);
    }
    onClose();
  };

  return (
    <div className="auth-modal-overlay" onClick={onClose} role="presentation">
      <div
        className="auth-modal-panel login-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="auth-modal-heading"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="auth-modal-close"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        <h2 id="auth-modal-heading" className="auth-modal-sr-only">
          {mode === 'signup' ? 'Sign up' : 'Sign in'}
        </h2>
        <AuthBrandHeader />
        <AuthModeTabs active={mode} onSelect={setMode} />
        {mode === 'signin' ? (
          <SignInForm
            idPrefix="auth-modal-"
            autoFocus={false}
            onSuccess={handleSuccess}
            onRequestSignup={() => setMode('signup')}
          />
        ) : (
          <SignUpForm
            idPrefix="auth-modal-"
            autoFocus={false}
            onSuccess={handleSuccess}
          />
        )}
      </div>
    </div>
  );
};

export default AuthModal;
